import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { salesPersonLogin } from '../../lib/api';
import { useTracker } from '../../context/TrackerContext';

/* ── Data ────────────────────────────────────────────────────────────────── */
const TIERS = [
  { name:'Diplomat', emoji:'👑', sales:18, pct:20, color:'#7c3aed', bg:'linear-gradient(135deg,#4c1d95,#7c3aed)' },
  { name:'Diamond',  emoji:'💎', sales:15, pct:15, color:'#0ea5e9', bg:'linear-gradient(135deg,#075985,#0ea5e9)' },
  { name:'Gold',     emoji:'🥇', sales:10, pct:10, color:'#d97706', bg:'linear-gradient(135deg,#92400e,#f59e0b)' },
  { name:'Silver',   emoji:'🥈', sales:7,  pct:7,  color:'#64748b', bg:'linear-gradient(135deg,#334155,#94a3b8)' },
  { name:'Bronze',   emoji:'🥉', sales:5,  pct:5,  color:'#b45309', bg:'linear-gradient(135deg,#78350f,#d97706)' },
];

const QUOTES = [
  { text:'Every call you make is one step closer to your best week.', icon:'📞' },
  { text:'Champions don\'t wait for opportunity — they create it.', icon:'🏆' },
  { text:'Your effort today is your reward tomorrow.', icon:'💪' },
  { text:'One more sale. That\'s all it takes to change everything.', icon:'🔥' },
  { text:'You are one conversation away from changing a student\'s life.', icon:'🌟' },
  { text:'The difference between winning and losing is who quits first.', icon:'⚡' },
];

const GREETINGS = (() => {
  const h = new Date().getHours();
  if (h < 12) return { word: 'Good Morning', emoji: '☀️', sub: 'Let\'s make today count!' };
  if (h < 17) return { word: 'Good Afternoon', emoji: '⚡', sub: 'Afternoon hustle hits different!' };
  return { word: 'Good Evening', emoji: '🌙', sub: 'Evening warriors close the biggest deals!' };
})();

/* ── Component ───────────────────────────────────────────────────────────── */
const SalesLogin: React.FC = () => {
  const navigate = useNavigate();
  const { loginAsSalesPerson } = useTracker();
  const [passcode, setPasscode] = useState('');
  const [loading, setLoading]   = useState(false);
  const [show, setShow]         = useState(false);
  const [quoteIdx, setQuoteIdx] = useState(0);
  const [quoteVis, setQuoteVis] = useState(true);
  const [pulse, setPulse]       = useState(0);
  const rafRef = useRef<number>(0);

  /* Rotating quote */
  useEffect(() => {
    const id = setInterval(() => {
      setQuoteVis(false);
      setTimeout(() => { setQuoteIdx(i => (i + 1) % QUOTES.length); setQuoteVis(true); }, 500);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  /* Ambient pulse for orbs */
  useEffect(() => {
    let t = 0;
    const loop = () => { t++; setPulse(Math.sin(t / 60)); rafRef.current = requestAnimationFrame(loop); };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passcode.trim()) { toast.error('Enter your passcode'); return; }
    setLoading(true);
    try {
      const res = await salesPersonLogin(passcode.trim());
      if (!res.ok) { toast.error(res.error || 'Invalid passcode'); return; }
      loginAsSalesPerson(res.salesPersonId!, res.name!, res.email || '', { crmToken: res.crmToken, salesRole: res.salesRole, takesLeads: res.takesLeads });
      toast.success(`Welcome, ${res.name}! Let's crush it today 🔥`);
      navigate('/tracker/sales/dashboard');
    } catch (err: any) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const CSS = `
    @keyframes gradShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
    @keyframes floatUp   { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-18px)} }
    @keyframes floatDown { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(14px)} }
    @keyframes fadeIn    { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
    @keyframes fadeInQ   { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
    @keyframes shimmer   { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
    @keyframes popIn     { 0%{transform:scale(.85);opacity:0} 80%{transform:scale(1.04)} 100%{transform:scale(1);opacity:1} }
    @keyframes btnGlow   { 0%,100%{box-shadow:0 8px 28px rgba(109,40,217,.5)} 50%{box-shadow:0 8px 40px rgba(109,40,217,.75)} }
    .sl-fadeIn  { animation: fadeIn .6s ease both }
    .sl-quote   { transition: opacity .45s, transform .45s; }
    .sl-quote.hidden { opacity:0; transform:translateY(8px); }
    .sl-quote.shown  { opacity:1; transform:none; }
    .sl-tier:hover { transform:translateY(-2px) scale(1.02); box-shadow:0 8px 24px rgba(0,0,0,.25) !important; }
    .sl-tier { transition: all .18s; }
    .sl-input:focus { border-color:#a78bfa !important; box-shadow:0 0 0 3px rgba(167,139,250,.25) !important; }
    .sl-btn:not(:disabled):hover { filter:brightness(1.08); transform:translateY(-1px); }
    .sl-btn:not(:disabled):active { transform:scale(.97); }
    .sl-btn { transition: all .15s; }
  `;

  return (
    <div style={{ minHeight:'100vh', display:'flex', fontFamily:'"Plus Jakarta Sans",Inter,system-ui,sans-serif', overflow:'hidden', position:'relative' }}>
      <style>{CSS}</style>

      {/* ── Left: Hero panel ── */}
      <div style={{
        width:'55%', minWidth:320, position:'relative', overflow:'hidden',
        background:'linear-gradient(135deg,#0f172a 0%,#1e1b4b 30%,#4c1d95 65%,#6d28d9 100%)',
        backgroundSize:'300% 300%', animation:'gradShift 8s ease infinite',
        display:'flex', flexDirection:'column', justifyContent:'center', padding:'48px 52px',
      }}>
        {/* Floating orbs */}
        <div style={{ position:'absolute', inset:0, pointerEvents:'none', overflow:'hidden' }}>
          <div style={{ position:'absolute', width:420, height:420, borderRadius:'50%', background:'rgba(167,139,250,.12)', top:-140, right:-100, transform:`translateY(${pulse*18}px)`, transition:'transform .1s linear' }} />
          <div style={{ position:'absolute', width:280, height:280, borderRadius:'50%', background:'rgba(236,72,153,.1)', bottom:-80, left:-60, transform:`translateY(${-pulse*14}px)`, transition:'transform .1s linear' }} />
          <div style={{ position:'absolute', width:160, height:160, borderRadius:'50%', background:'rgba(251,191,36,.08)', top:'40%', left:'15%', transform:`translateY(${pulse*10}px)`, transition:'transform .1s linear' }} />
          {/* Grid lines */}
          <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', opacity:.06 }}>
            {Array.from({length:12},(_,i)=>(
              <line key={i} x1={0} y1={`${i*9}%`} x2="100%" y2={`${i*9}%`} stroke="white" strokeWidth=".5"/>
            ))}
            {Array.from({length:10},(_,i)=>(
              <line key={i} x1={`${i*11}%`} y1={0} x2={`${i*11}%`} y2="100%" stroke="white" strokeWidth=".5"/>
            ))}
          </svg>
        </div>

        {/* Content */}
        <div style={{ position:'relative' }}>
          {/* Logo */}
          <div className="sl-fadeIn" style={{ display:'flex', alignItems:'center', gap:10, marginBottom:40 }}>
            <div style={{ width:44, height:44, borderRadius:14, background:'rgba(255,255,255,.15)', backdropFilter:'blur(10px)', display:'flex', alignItems:'center', justifyContent:'center', border:'1.5px solid rgba(255,255,255,.25)' }}>
              <span style={{ fontSize:22 }}>🎯</span>
            </div>
            <div>
              <p style={{ fontSize:14, fontWeight:900, color:'#fff', letterSpacing:'.01em' }}>VistaTech</p>
              <p style={{ fontSize:10, color:'rgba(255,255,255,.55)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em' }}>Sales Portal</p>
            </div>
          </div>

          {/* Greeting */}
          <div className="sl-fadeIn" style={{ marginBottom:28 }}>
            <p style={{ fontSize:13, color:'rgba(255,255,255,.6)', fontWeight:700, marginBottom:8, letterSpacing:'.04em' }}>
              {GREETINGS.emoji} {GREETINGS.word}
            </p>
            <h1 style={{ fontSize:38, fontWeight:900, color:'#fff', lineHeight:1.12, letterSpacing:'-.03em', marginBottom:8, textShadow:'0 4px 24px rgba(0,0,0,.3)' }}>
              Ready to<br/>
              <span style={{ background:'linear-gradient(90deg,#fbbf24,#f472b6,#a78bfa)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>
                Crush Today?
              </span> 🔥
            </h1>
            <p style={{ fontSize:13, color:'rgba(255,255,255,.65)', fontWeight:600, lineHeight:1.5 }}>{GREETINGS.sub}</p>
          </div>

          {/* Rotating quote */}
          <div style={{ background:'rgba(255,255,255,.08)', backdropFilter:'blur(10px)', borderRadius:16, padding:'18px 20px', marginBottom:36, border:'1px solid rgba(255,255,255,.15)', minHeight:82 }}>
            <div className={`sl-quote ${quoteVis?'shown':'hidden'}`}>
              <p style={{ fontSize:18, marginBottom:6 }}>{QUOTES[quoteIdx].icon}</p>
              <p style={{ fontSize:13, color:'rgba(255,255,255,.9)', fontStyle:'italic', lineHeight:1.65, fontWeight:500 }}>
                "{QUOTES[quoteIdx].text}"
              </p>
            </div>
          </div>

          {/* Tier ladder */}
          <div>
            <p style={{ fontSize:10, fontWeight:800, color:'rgba(255,255,255,.45)', textTransform:'uppercase', letterSpacing:'.1em', marginBottom:12 }}>
              🏆 Weekly Incentive Tiers
            </p>
            <div style={{ display:'flex', gap:8 }}>
              {TIERS.map((t, i) => (
                <div key={t.name} className="sl-tier" style={{
                  flex:1, borderRadius:12, padding:'10px 6px', background:t.bg,
                  textAlign:'center', cursor:'default',
                  boxShadow:'0 4px 12px rgba(0,0,0,.3)',
                  animation:`popIn .4s ${i*.08}s ease both`,
                }}>
                  <p style={{ fontSize:16, marginBottom:2 }}>{t.emoji}</p>
                  <p style={{ fontSize:14, fontWeight:900, color:'#fff', lineHeight:1 }}>{t.pct}%</p>
                  <p style={{ fontSize:8, color:'rgba(255,255,255,.75)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.04em', marginTop:3 }}>{t.name}</p>
                  <p style={{ fontSize:8, color:'rgba(255,255,255,.5)', marginTop:2 }}>{t.sales}+ sales</p>
                </div>
              ))}
            </div>
            <p style={{ fontSize:10, color:'rgba(255,255,255,.35)', marginTop:10, fontWeight:600 }}>
              Tiers reset every Monday · Incentive paid on fully collected fees only
            </p>
          </div>
        </div>
      </div>

      {/* ── Right: Login form ── */}
      <div style={{
        flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        background:'#f8fafc', padding:'40px 32px', position:'relative', overflow:'hidden',
      }}>
        {/* Subtle bg pattern */}
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(#e2e8f0 1px, transparent 1px)', backgroundSize:'28px 28px', opacity:.5, pointerEvents:'none' }} />

        <div className="sl-fadeIn" style={{ width:'100%', maxWidth:360, position:'relative' }}>

          {/* Card */}
          <div style={{ background:'#fff', borderRadius:24, padding:'36px 32px', boxShadow:'0 20px 60px rgba(0,0,0,.1), 0 4px 16px rgba(0,0,0,.06)', border:'1px solid #f1f5f9' }}>

            {/* Avatar ring */}
            <div style={{ textAlign:'center', marginBottom:28 }}>
              <div style={{ width:72, height:72, borderRadius:'50%', background:'linear-gradient(135deg,#6d28d9,#a855f7,#ec4899)', margin:'0 auto 14px', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 8px 28px rgba(109,40,217,.4)', animation:'btnGlow 3s ease-in-out infinite' }}>
                <span style={{ fontSize:32 }}>⚡</span>
              </div>
              <h2 style={{ fontSize:22, fontWeight:900, color:'#0f172a', marginBottom:4, letterSpacing:'-.02em' }}>
                Welcome Back!
              </h2>
              <p style={{ fontSize:13, color:'#94a3b8', fontWeight:600 }}>
                Sign in to your sales dashboard
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8 }}>
                  Your Passcode
                </label>
                <div style={{ position:'relative' }}>
                  <input
                    className="sl-input"
                    type={show ? 'text' : 'password'}
                    value={passcode}
                    onChange={e => setPasscode(e.target.value)}
                    placeholder="Enter your passcode"
                    autoFocus
                    style={{
                      width:'100%', boxSizing:'border-box',
                      padding:'13px 50px 13px 16px',
                      borderRadius:12, border:'1.5px solid #e2e8f0',
                      fontSize:16, fontWeight:700, color:'#0f172a',
                      outline:'none', fontFamily:'inherit', letterSpacing:'.06em',
                      transition:'border-color .15s, box-shadow .15s',
                      background:'#f8fafc',
                    }}
                  />
                  <button type="button" onClick={() => setShow(s => !s)}
                    style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:18, lineHeight:1, padding:4, color:'#94a3b8' }}>
                    {show ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className="sl-btn"
                style={{
                  width:'100%', padding:'14px', borderRadius:12, border:'none', cursor: loading?'not-allowed':'pointer',
                  fontSize:15, fontWeight:900, color:'#fff', fontFamily:'inherit',
                  background: loading
                    ? 'linear-gradient(135deg,#a78bfa,#c084fc)'
                    : 'linear-gradient(135deg,#6d28d9,#a855f7,#ec4899)',
                  backgroundSize:'200% 200%', animation: loading?'gradShift 1.5s ease infinite':'btnGlow 3s ease-in-out infinite',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                  letterSpacing:'.01em',
                }}>
                {loading ? (
                  <>
                    <div style={{ width:16, height:16, borderRadius:'50%', border:'2.5px solid rgba(255,255,255,.35)', borderTopColor:'#fff', animation:'spin 0.7s linear infinite' }} />
                    Signing you in…
                  </>
                ) : (
                  <>🚀 Let's Go!</>
                )}
              </button>
            </form>

            {/* Divider */}
            <div style={{ display:'flex', alignItems:'center', gap:10, margin:'24px 0 18px' }}>
              <div style={{ flex:1, height:1, background:'#f1f5f9' }} />
              <p style={{ fontSize:10, color:'#cbd5e1', fontWeight:700 }}>THIS WEEK'S GOAL</p>
              <div style={{ flex:1, height:1, background:'#f1f5f9' }} />
            </div>

            {/* Mini tier strip */}
            <div style={{ display:'flex', gap:6 }}>
              {TIERS.map(t => (
                <div key={t.name} style={{ flex:1, textAlign:'center', padding:'8px 4px', borderRadius:10, background:'#f8fafc', border:'1px solid #f1f5f9' }}>
                  <p style={{ fontSize:14 }}>{t.emoji}</p>
                  <p style={{ fontSize:11, fontWeight:900, color:'#0f172a', marginTop:2 }}>{t.pct}%</p>
                  <p style={{ fontSize:8, color:'#94a3b8', fontWeight:700 }}>{t.sales}+</p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer note */}
          <p style={{ textAlign:'center', fontSize:11, color:'#cbd5e1', marginTop:20, fontWeight:600 }}>
            Don't have a passcode? Contact your admin.
          </p>
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default SalesLogin;
